"use client";

import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

// Lazy load next-auth/react to avoid SSR issues
// Webpack config now handles client-only modules properly
let _getSessionModule: any = null;

// Cache session to prevent excessive API calls
let _sessionCache: { session: any; timestamp: number } | null = null;
let _sessionPromise: Promise<any> | null = null; // Promise cache to prevent concurrent calls
let _isFetchingSession = false; // Lock flag to prevent concurrent fetches
const SESSION_CACHE_TTL = 10000; // Cache for 10 seconds (increased to reduce calls significantly)

// Synchronous mutex to prevent race conditions in prepareHeaders
let _prepareHeadersLock = false;
const API_DEBUG = process.env.NODE_ENV === "development" && process.env.NEXT_PUBLIC_DEBUG_API === "1";

async function getSessionWithRetry(maxRetries = 15, initialDelay = 100, delay = 200, useCache = true): Promise<any> {
	// Return null immediately on server side to prevent SSR issues
	if (typeof window === "undefined") {
		return null;
	}

	// Check cache first to prevent excessive API calls
	if (useCache && _sessionCache) {
		const cacheAge = Date.now() - _sessionCache.timestamp;
		if (cacheAge < SESSION_CACHE_TTL && _sessionCache.session) {
			return _sessionCache.session;
		}
	}
	
	// If there's already a pending request, wait for it instead of making a new one
	if (useCache && _sessionPromise) {
		return _sessionPromise;
	}
	
	// If we're already fetching, wait a bit and check again (prevents race conditions)
	if (useCache && _isFetchingSession) {
		// Wait a short time and check promise again
		await new Promise(resolve => setTimeout(resolve, 50));
		if (_sessionPromise) {
			return _sessionPromise;
		}
	}

	// Set lock flag BEFORE any async operations
	if (useCache) {
		_isFetchingSession = true;
	}

	// Lazy load next-auth/react module only when function is called
	// Standard dynamic import - webpack config handles client-only modules
	if (!_getSessionModule) {
		try {
			_getSessionModule = await import("next-auth/react");
		} catch (error) {
			console.error("Failed to import next-auth/react:", error);
			if (useCache) {
				_isFetchingSession = false;
			}
			return null;
		}
	}

	const { getSession } = _getSessionModule;

	// Create a new promise that will be reused for concurrent calls
	// CRITICAL: Create and store promise synchronously BEFORE any async work
	const sessionPromise = (async () => {
		try {
			// Wait a bit initially to allow NextAuth to initialize
			// This is especially important on page refresh when cookies need to be read
			await new Promise(resolve => setTimeout(resolve, initialDelay));

			for (let i = 0; i < maxRetries; i++) {
				try {
					const session = await getSession();
					// CRITICAL: Only return session if idToken exists and is not empty
					// An empty idToken means the session is invalid for API calls
					if (session?.idToken && session.idToken.trim().length > 0) {
						// Cache the session result to prevent excessive API calls
						if (useCache) {
							_sessionCache = { session, timestamp: Date.now() };
						}
						return session;
					}
					// If session exists but no idToken, wait a bit and retry
					// This handles cases where session is loaded but idToken hasn't propagated yet
					if (session && i < maxRetries - 1) {
						// Exponential backoff: 200ms, 400ms, 600ms, etc.
						const currentDelay = delay * (i + 1);
						await new Promise(resolve => setTimeout(resolve, currentDelay));
						continue;
					}
					// If no session at all, wait and retry (might still be loading)
					if (!session && i < maxRetries - 1) {
						// Exponential backoff: 200ms, 400ms, 600ms, etc.
						const currentDelay = delay * (i + 1);
						await new Promise(resolve => setTimeout(resolve, currentDelay));
						continue;
					}
					// If we've exhausted retries and still no idToken, return null
					// This allows RTK Query to handle the error gracefully
					if (i === maxRetries - 1 && session && !session.idToken) {
						console.warn("Session exists but idToken is missing after all retries. Session may be invalid.");
					}
					
					// Cache even null/empty session to prevent repeated failed attempts
					if (useCache) {
						_sessionCache = { session, timestamp: Date.now() };
					}
					
					return session;
				} catch (error) {
					if (i === maxRetries - 1) {
						console.error("Failed to get session after retries:", error);
						// Don't throw on client side - return null to allow request to proceed
						return null;
					}
					// Exponential backoff on errors too
					const currentDelay = delay * (i + 1);
					await new Promise(resolve => setTimeout(resolve, currentDelay));
				}
			}
			return null;
		} finally {
			// Always clear lock and promise cache after completion
			if (useCache) {
				_isFetchingSession = false;
				_sessionPromise = null;
			}
		}
	})();
	
	// Store promise IMMEDIATELY for concurrent calls (before awaiting)
	if (useCache) {
		_sessionPromise = sessionPromise;
	}
	
	return sessionPromise;
}

const baseQueryWithAuth = fetchBaseQuery({
	baseUrl: `${process.env.NEXT_PUBLIC_BACKEND_URL || "https://api.techxeed.com"}/`,
	// NOTE: credentials is NOT set here because:
	// 1. It triggers CORS preflight requests that require backend OPTIONS handling
	// 2. Authorization is handled via Bearer tokens in headers, not cookies
	// 3. The X-Requested-With header is sufficient for CSRF protection
	prepareHeaders: async (headers, { type, endpoint }) => {
		try {
			// Return early on server side
			if (typeof window === "undefined") {
				return headers;
			}
			
			// DEBUG: Log when prepareHeaders is called
			if (API_DEBUG && endpoint) {
				console.log('[prepareHeaders] Called for:', { endpoint, type });
			}

			// List of endpoints that require authentication
			// These endpoints will wait for session before making the request
			const authenticatedEndpoints = [
				'getNotifications',
				'getNotification',
				'searchNotifications',
				'getAllChats',
				'getChat',
				'getCustomerSupportAccount',
				'getClientSupportGroupChat',
				'getClientSupportGroupMembers',
				'sendClientSupportGroupMessage',
				'getChatsMessagesById',
				'getUser',
				'getUsers',
				'searchUsers',
				'getAggregatedUsers',
				'getRefedUsers',
				'getAffiliatedUsers',
				'getAffiliatedUsersForCurrent',
				'getAggregatedUsersByUserId',
				'updateUser',
				'deleteUser',
				'assignRole',
				'getOrders',
				'getOrdersAggregated',
				'searchOrders',
				'getOrder',
				'getHostingUsage',
				'getBasketMe',
				'getItem',
				'createOrder',
				'updateOrder',
				'deleteOrder',
				'resetPassword',
				'getReceiptUrl',
				'getSavedBlogs',
				'getSavedServices',
				'getStripeURL',
				'get2FAStatus',
				'getBankAccountForCurrent',
				'getAffiliates',
				'searchReviews',
				'getCurrencyRatesAdmin',
				'getAdminCompanyReviews',
				'getGa4Dashboard',
				'getInternalAnalyticsDashboard',
				'getSupportTickets',
				'getSupportTicket',
				'getChatbotConversation',
				'getChatbotMessages',
				'getAdminChatbotConversations',
				'getAdminChatbotConversation',
				'getAdminChatbotMessages',
				'getAdminChatbotKnowledge',
				'getOrder',
				'confirmOrderCheckout',
			];
			
			// Check if this endpoint requires authentication
			const publicMutations = new Set([
				"createGuestOrder",
				"completeGuestCheckoutPassword",
				"completeGuestCheckoutSso",
				"validatePromoCode",
				"subscribeToNewsletter",
			]);
			const requiresAuth =
				(type === "mutation" && endpoint && !publicMutations.has(endpoint)) ||
				(endpoint && authenticatedEndpoints.includes(endpoint));

			if (
				endpoint === "createGuestOrder" ||
				endpoint === "completeGuestCheckoutPassword" ||
				endpoint === "completeGuestCheckoutSso"
			) {
				headers.set("X-Requested-With", "XMLHttpRequest");
			}

			// Never attach Bearer tokens to public endpoints. A revoked/expired session
			// would otherwise 401 public routes (e.g. company-reviews/public video list).
			if (!requiresAuth) {
				if (!_sessionPromise && !_prepareHeadersLock) {
					_prepareHeadersLock = true;
					getSessionWithRetry().finally(() => {
						_prepareHeadersLock = false;
					});
				}
			} else if (_sessionCache) {
				// Authenticated only: use cache first (sync) before any async wait
				const cacheAge = Date.now() - _sessionCache.timestamp;
				if (cacheAge < SESSION_CACHE_TTL && _sessionCache.session?.idToken) {
					headers.set("Authorization", `Bearer ${_sessionCache.session.idToken}`);
					if (type === 'mutation') {
						headers.set("X-Requested-With", "XMLHttpRequest");
					}
					if (API_DEBUG && endpoint) {
						console.log('[prepareHeaders] Using cached session for:', endpoint);
					}
					return headers;
				}
			}
			
			if (requiresAuth) {
				// For authenticated endpoints, wait for session before proceeding
				// This prevents 401 errors and unnecessary retries
				try {
					const session = await getSessionWithRetry(10, 100, 200, true);
					if (session?.idToken && session.idToken.trim().length > 0) {
						headers.set("Authorization", `Bearer ${session.idToken}`);
						if (type === 'mutation') {
							headers.set("X-Requested-With", "XMLHttpRequest");
						}
						if (API_DEBUG && endpoint) {
							console.log('[prepareHeaders] Using session for authenticated endpoint:', endpoint);
							// Log token info for debugging (first 20 chars only for security)
							const tokenPreview = session.idToken.substring(0, 20) + '...';
							console.log('[prepareHeaders] Token preview:', tokenPreview, 'Length:', session.idToken.length);
						}
						return headers;
					} else {
						// Session not available yet, but endpoint requires auth
						// Log warning but proceed (will get 401 and retry)
						if (API_DEBUG && endpoint) {
							console.warn('[prepareHeaders] Session not available for authenticated endpoint:', endpoint);
							console.warn('[prepareHeaders] Session object:', session ? 'exists but no idToken' : 'null/undefined');
						}
					}
				} catch (error) {
					// If session fetch fails, proceed without auth (will retry with 401 handler)
					if (API_DEBUG && endpoint) {
						console.warn('[prepareHeaders] Failed to get session for:', endpoint, error);
					}
				}
			}
			
			// CORS FIX: Remove Content-Type from GET requests (queries) to avoid preflight
			if (type === 'query' && headers.get('Content-Type') === 'application/json') {
				headers.delete('Content-Type');
			}
			
			// SECURITY: Add X-Requested-With header for CSRF protection (only for mutations)
			if (type === 'mutation') {
				headers.set("X-Requested-With", "XMLHttpRequest");
			}
			
			if (API_DEBUG && endpoint && !requiresAuth) {
				console.log('[prepareHeaders] Proceeding without auth for public endpoint:', endpoint);
			}
			
			return headers;
		} catch (error) {
			console.error("Unexpected error in prepareHeaders:", error);
			// Always return headers even on error to prevent blocking requests
			if (type === 'mutation') {
				headers.set("X-Requested-With", "XMLHttpRequest");
			}
			return headers;
		}
	},
});

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
	args,
	api,
	extraOptions,
) => {
	// DEBUG: Log when baseQuery is called
	const url = typeof args === 'string' ? args : args.url;
	if (API_DEBUG) {
		console.log('[baseQuery] Called for:', url);
	}
	
	// Retry logic for 401 errors - might be a transient session issue
	let result = await baseQueryWithAuth(args, api, extraOptions);
	
	// Improve error structure for 400 errors - ensure error details are properly extracted
	if (result.error && result.error.status === 400) {
		// fetchBaseQuery returns errors in a specific structure
		// Ensure we have proper error details for better debugging
		if (result.error && typeof result.error === 'object') {
			const error = result.error as any;
			// If error.data exists but isn't properly structured, extract it
			if (error.data && typeof error.data === 'object') {
				// NestJS returns error details in error.data.message or error.data
				if (error.data.message && !error.message) {
					error.message = error.data.message;
				}
				// Preserve the full error data for debugging
				error.errorData = error.data;
			}
			// Ensure statusText is set if available
			if (!error.statusText && error.status === 400) {
				error.statusText = 'Bad Request';
			}
		}
	}
	
	// If we get a 401, wait a bit and try once more with a fresh session
	// This handles the case where the session is still loading when the initial request is made
	if (result.error && result.error.status === 401) {
		// Wait a bit to allow session to load (especially important on page refresh)
		await new Promise(resolve => setTimeout(resolve, 500));
		
			// Try to get a fresh session and retry once with the fresh token
			// Don't use cache for retry - we want a fresh session
			try {
				const session = await getSessionWithRetry(10, 0, 200, false); // Shorter retries for the retry attempt, bypass cache
				if (session?.idToken && session.idToken.trim().length > 0) {
					if (process.env.NODE_ENV === 'development') {
						console.debug("401 Error: Retrying request with fresh session token", {
							endpoint: typeof args === 'string' ? args : args.url,
						});
					}
					
					// Extract original headers from args to preserve custom headers like x-currency
					// RTK Query passes headers in args.headers, which can be an object or Headers instance
					let originalHeaders: Record<string, string> = {};
					if (typeof args !== 'string' && args.headers) {
						if (args.headers instanceof Headers) {
							// Convert Headers instance to plain object
							args.headers.forEach((value, key) => {
								originalHeaders[key] = value;
							});
						} else if (typeof args.headers === 'object' && !Array.isArray(args.headers)) {
							// Plain object - filter out undefined values
							originalHeaders = Object.fromEntries(
								Object.entries(args.headers).filter(([_, value]) => value !== undefined)
							) as Record<string, string>;
						}
					}
					
				// Create a custom fetchBaseQuery with the fresh token to avoid prepareHeaders calling getSession again
				const retryQuery = fetchBaseQuery({
					baseUrl: `${process.env.NEXT_PUBLIC_BACKEND_URL || "https://api.techxeed.com"}/`,
					// No credentials needed - using Bearer token authentication
					prepareHeaders: (headers, { type }) => {
						// Preserve original headers (like x-currency) from the original request
						// These headers are set BEFORE Authorization so they don't get overwritten
						Object.entries(originalHeaders).forEach(([key, value]) => {
							if (value !== undefined && value !== null && value !== '') {
								headers.set(key, String(value));
							}
						});
						// Use the fresh token directly instead of calling getSession again
						headers.set("Authorization", `Bearer ${session.idToken}`);
						// Only add X-Requested-With for mutations, not GET requests
						if (type === 'mutation') {
							headers.set("X-Requested-With", "XMLHttpRequest");
						}
						// Remove Content-Type from GET requests to avoid preflight
						if (type === 'query' && headers.get('Content-Type') === 'application/json') {
							headers.delete('Content-Type');
						}
						return headers;
					},
				});
				// Retry the request with the fresh token
				// Note: args already contains headers, but prepareHeaders will merge them properly
				result = await retryQuery(args, api, extraOptions);
				
				// If retry also fails with 401, the token is likely expired
				if (result.error && result.error.status === 401) {
					// Clear session cache to force fresh session on next request
					_sessionCache = null;
					_sessionPromise = null;
					
					// Only log in development to reduce noise
					// Use warn instead of error since token expiration is expected behavior
					if (process.env.NODE_ENV === 'development') {
						console.warn("401 Error: Retry also failed. Token may be expired. User needs to re-authenticate.", {
							endpoint: typeof args === 'string' ? args : args.url,
						});
					}
					// Mark the error so components can handle it (e.g., redirect to login)
					if (result.error && typeof result.error === 'object') {
						(result.error as any).tokenExpired = true;
					}
				}
			} else {
				// Clear session cache to force fresh session on next request
				_sessionCache = null;
				_sessionPromise = null;
				
				// Only log in development to reduce noise
				// This is expected during initial page load when session is still loading
				if (process.env.NODE_ENV === 'development') {
					console.warn("401 Error: No session or idToken available after retry. This may be expected during initial page load.", {
						endpoint: typeof args === 'string' ? args : args.url,
					});
				}
				// Mark the error so components can handle it
				if (result.error && typeof result.error === 'object') {
					(result.error as any).tokenExpired = true;
				}
			}
		} catch (error) {
			// Clear session cache to force fresh session on next request
			_sessionCache = null;
			_sessionPromise = null;
			
			// Only log in development to reduce noise
			if (process.env.NODE_ENV === 'development') {
				console.warn("Failed to verify session after 401:", error);
			}
			// Mark the error so components can handle it
			if (result.error && typeof result.error === 'object') {
				(result.error as any).tokenExpired = true;
			}
		}
	}
	
	return result;
};

/** Clears cached NextAuth session used by RTK prepareHeaders (e.g. before checkout). */
export function invalidateApiSessionCache() {
	_sessionCache = null;
	_sessionPromise = null;
	_isFetchingSession = false;
}

export const Api = createApi({
	baseQuery,
	tagTypes: [
		"order",
		"order-notes",
		"category",
		"chats",
		"chat-messages",
		"saved",
		"blog",
		"blog-comments",
		"blog-saved",
		"blog-likes",
		"BlogCategories",
		"Keywords",
		"affiliates",
		"user",
		"user-notes",
		"plans",
		"services",
		"services-frequently-bought",
		"services-saved",
		"notifications",
		"bank-accounts",
		"currency-rates",
		"company-reviews",
		"FAQ",
		"PromoCodes",
		"Newsletter",
		"CompaniesHouse",
		"Crm",
		"UserCrm",
		"Automation",
		"calendar",
		"analytics",
		"support-tickets",
		"compliance-policies",
		"chatbot-conversations",
		"chatbot-messages",
		"chatbot-knowledge",
		"basket",
	],
	endpoints: (builder) => ({}),
});
